import React, { useState } from 'react';
import { OperaWindow, OperaSection, OperaField, type SideButton } from './OperaWindow';
import { api } from '../lib/api';

// ===========================================================================
// Create a service provider profile. MANAGER+ only (the server is the real
// gate; the entry point is hidden for other roles).
//
// The profile's property is NOT chosen here — the server takes it from the
// caller's current property context, so a profile always belongs to the
// property the manager is switched into.
//
// Portal access is a second, optional step: create the profile, then call
// grant-portal-access, which owns username/password/assignedPropertyId. That
// keeps all credential handling in the one endpoint that already does it.
// ===========================================================================

interface Props {
  onClose: () => void;
  onCreated: (profileId: string) => void;
}

interface Credentials {
  username: string;
  tempPassword: string;
}

export function NewProfileModal({ onClose, onCreated }: Props) {
  const [workingName, setWorkingName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [notes, setNotes] = useState('');
  const [enablePortal, setEnablePortal] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set once creation succeeds — from here the profile exists and closing the
  // modal must not read as a cancel.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [createdName, setCreatedName] = useState('');
  const [credentials, setCredentials] = useState<Credentials | null>(null);
  // Profile created but the portal-access call failed — recoverable, and the
  // user needs to be told exactly how.
  const [grantError, setGrantError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<'username' | 'password' | null>(null);

  function copyToClipboard(text: string, which: 'username' | 'password') {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopiedField(which);
        setTimeout(() => setCopiedField(null), 2000);
      }).catch(() => {
        setError('Could not copy to clipboard.');
      });
    }
  }

  async function handleCreate() {
    const name = workingName.trim();
    const mail = email.trim();
    const tel = phone.trim();

    if (!name) { setError('Working name is required.'); return; }
    // Mirrors what grant-portal-access enforces server-side: the email becomes
    // the portal username and the phone is used for shift notifications.
    if (enablePortal) {
      if (!mail) { setError('Email is required to enable portal login — it becomes the username.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) { setError('Enter a valid email address.'); return; }
      if (!tel) { setError('Phone is required to enable portal login.'); return; }
    }
    if (dateOfBirth && !/^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
      setError('Enter a valid date of birth.');
      return;
    }

    setBusy(true); setError(null);

    let profileId: string;
    try {
      const created = await api.createProfile({
        workingName: name,
        email: mail || null,
        phone: tel || null,
        dateOfBirth: dateOfBirth || null,
        notes: notes.trim() || null,
      });
      profileId = created.id;
      setCreatedId(created.id);
      setCreatedName(created.workingName);
    } catch (e) {
      // Nothing was created — stay on the form so the input isn't lost.
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
      return;
    }

    if (enablePortal) {
      try {
        const res = await api.grantPortalAccess(profileId);
        setCredentials({ username: res.profile.username, tempPassword: res.tempPassword });
      } catch (e) {
        // The profile exists; only the credentials failed. Recoverable via
        // Grant Portal Access on the profile itself, which is idempotent.
        setGrantError(e instanceof Error ? e.message : String(e));
      }
    }

    setBusy(false);
  }

  function handleDone() {
    if (createdId) onCreated(createdId);
    else onClose();
  }

  const sideButtons: SideButton[] = createdId
    ? [{ label: 'Done', onClick: handleDone, primary: true }]
    : [
        { label: 'Create', onClick: handleCreate, primary: true, disabled: busy },
        { label: 'Cancel', onClick: onClose, disabled: busy },
      ];

  // ---- Result view: the profile exists, don't offer the form again --------
  if (createdId) {
    return (
      <OperaWindow title="PROFILE CREATED" onClose={handleDone} sideButtons={sideButtons}>
        {error && <div className="modal-error">{error}</div>}

        <OperaSection title="Created">
          <p style={{ margin: '4px 0 14px', fontSize: 12 }}>
            <strong>{createdName}</strong> has been created at your current property.
          </p>
        </OperaSection>

        {credentials && (
          <OperaSection title="Portal Access Granted">
            <p style={{ margin: '4px 0 14px', fontSize: 12 }}>
              Share these credentials with <strong>{createdName}</strong>. They will be prompted to
              change the password on first login.
            </p>

            <OperaField label="Username (email)">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 13, flex: 1 }}>{credentials.username}</span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(credentials.username, 'username')}
                  style={{ fontSize: 11, padding: '2px 8px' }}
                >
                  {copiedField === 'username' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </OperaField>

            <OperaField label="Temporary password">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 600, letterSpacing: 1, flex: 1 }}>
                  {credentials.tempPassword}
                </span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(credentials.tempPassword, 'password')}
                  style={{ fontSize: 11, padding: '2px 8px' }}
                >
                  {copiedField === 'password' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </OperaField>

            <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text-secondary)' }}>
              This password is shown only once. If lost, use Reset Portal on the profile to generate a new one.
            </p>
          </OperaSection>
        )}

        {grantError && (
          <OperaSection title="Portal Access Not Granted">
            <div className="modal-error">{grantError}</div>
            <p style={{ margin: '10px 0 0', fontSize: 12 }}>
              The profile was created successfully, but portal access was not. Open{' '}
              <strong>{createdName}</strong> and use <strong>Grant Portal Access</strong> to retry —
              nothing needs to be undone first.
            </p>
          </OperaSection>
        )}
      </OperaWindow>
    );
  }

  // ---- Form view ----------------------------------------------------------
  return (
    <OperaWindow title="NEW PROFILE" onClose={onClose} sideButtons={sideButtons}>
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Identity">
        <OperaField label="Working name" required>
          <input
            type="text"
            value={workingName}
            onChange={e => setWorkingName(e.target.value)}
            maxLength={100}
            autoFocus
          />
        </OperaField>
        <OperaField label="Date of birth">
          <input
            type="date"
            value={dateOfBirth}
            onChange={e => setDateOfBirth(e.target.value)}
          />
        </OperaField>
      </OperaSection>

      <OperaSection title="Contact">
        <OperaField
          label="Email"
          required={enablePortal}
          hint={enablePortal ? 'Becomes the portal username' : undefined}
        >
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            maxLength={200}
          />
        </OperaField>
        <OperaField
          label="Phone"
          required={enablePortal}
          hint={enablePortal ? 'Used for shift notifications' : undefined}
        >
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            maxLength={40}
          />
        </OperaField>
      </OperaSection>

      <OperaSection title="Portal Access">
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={enablePortal}
            onChange={e => setEnablePortal(e.target.checked)}
          />
          Enable staff portal login
        </label>
        <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--text-secondary)' }}>
          Generates a temporary password and assigns this profile to your current property, so they
          can declare availability. Shown once, on the next screen. Can also be done later from the
          profile.
        </p>
      </OperaSection>

      <OperaSection title="Notes">
        <OperaField label="Internal notes">
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="optional"
            maxLength={500}
          />
        </OperaField>
      </OperaSection>
    </OperaWindow>
  );
}
